import { useState } from "react";
import { Button, CircularProgress, TextField } from "@mui/material";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { queryClient } from "queryClient";
import { toDate } from "helpers/dateHelper";
import ticketsService from "services/admin/ticketsService";
import { useGetTicketMessages } from "hooks/useGetTicketMessages";

interface TicketNotesProps {
  ticketID: number | string;
}

// Внутренние заметки по обращению. Под капотом — те же TicketMessage с
// isInternal=true, что и приватные сообщения сотрудников. Лента append-only:
// заметки не редактируются и не удаляются (так же, как обычная переписка),
// чтобы сохранить аудит-трейл и совпасть с семантикой TicketMessage.
export const TicketNotes = ({ ticketID }: TicketNotesProps) => {
  const { t } = useTranslation();
  const { data: messages = [], isLoading, isError } = useGetTicketMessages(ticketID);
  const notes = messages.filter((m: any) => m?.isInternal);

  const [draft, setDraft] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAdd = async () => {
    const content = draft.trim();
    if (!content) return;
    setIsSubmitting(true);
    try {
      await ticketsService.sendMessage(ticketID, { content, isInternal: true });
      setDraft("");
      queryClient.invalidateQueries({ queryKey: ["tickets", ticketID, "messages"] });
      queryClient.invalidateQueries({ queryKey: ["events", "ticket", ticketID] });
    } catch (err: any) {
      console.error(err);
      toast.error(err?.response?.data?.message || t("ticket.note.saveError"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      {isLoading && (
        <div className="flex items-center justify-center py-4">
          <CircularProgress size={18} />
        </div>
      )}

      {isError && <div className="text-sm text-red-500">{t("ticket.note.loadError")}</div>}

      {!isLoading && !isError && notes.length === 0 && (
        <div className="text-sm text-gray-500">{t("ticket.note.empty")}</div>
      )}

      {notes.map((note: any) => {
        const authorName = note.member?.name || t("ticket.note.unknownAuthor");
        return (
          <div
            key={note.id}
            style={{
              background: "#FFF8E1",
              border: "1px solid #FFE082",
              borderRadius: 8,
              padding: 10,
            }}
          >
            <div className="text-xs text-gray-600 mb-1">
              <span style={{ fontWeight: 600 }}>{authorName}</span>
              <span> · {toDate(note.createdAt)}</span>
            </div>
            <div className="text-sm" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
              {note.content}
            </div>
          </div>
        );
      })}

      <div className="flex flex-col gap-2 mt-1">
        <TextField
          size="small"
          multiline
          minRows={2}
          placeholder={t("ticket.note.placeholder")}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
        />
        <div className="flex justify-end">
          <Button
            size="small"
            variant="contained"
            color="primary"
            disabled={!draft.trim() || isSubmitting}
            onClick={handleAdd}
          >
            {t("ticket.note.add")}
          </Button>
        </div>
      </div>
    </div>
  );
};
